import { useState } from 'react';
import { Heart, Clock, Database, Trash2, RefreshCw, Info } from 'lucide-react';
import { useFavoritesStore } from '@/store/useFavoritesStore';
import { useRecentsStore } from '@/store/useRecentsStore';
import { useSettingsStore } from '@/store/useSettingsStore';
import { useChannelsStore } from '@/store/useChannelsStore';
import { cacheClearAll } from '@/lib/cache';

type ConfirmKey = 'favorites' | 'recents' | 'all' | null;

export function SettingsPage() {
  const favoriteIds = useFavoritesStore((s) => s.favoriteIds);
  const clearFavorites = useFavoritesStore((s) => s.clearFavorites);
  const recentEntries = useRecentsStore((s) => s.recentEntries);
  const clearRecents = useRecentsStore((s) => s.clearRecents);
  const autoplay = useSettingsStore((s) => s.autoplay);
  const setAutoplay = useSettingsStore((s) => s.setAutoplay);
  const lastUpdated = useChannelsStore((s) => s.lastUpdated);
  const isRefreshing = useChannelsStore((s) => s.isRefreshing);
  const refresh = useChannelsStore((s) => s.refresh);

  const [confirming, setConfirming] = useState<ConfirmKey>(null);

  const handleClearAll = async () => {
    clearFavorites();
    clearRecents();
    await cacheClearAll();
    setConfirming(null);
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h1 className="text-2xl font-bold mb-2">Settings</h1>
        <p className="text-text-muted">Configure your StreamPitch experience.</p>
      </div>

      {/* Playback */}
      <section className="bg-bg-elevated border border-border rounded-lg p-4 space-y-4">
        <h2 className="text-lg font-semibold">Playback</h2>
        <label className="flex items-center justify-between gap-3">
          <div>
            <div className="text-sm font-medium">Autoplay on channel select</div>
            <div className="text-xs text-text-muted">
              When you click a channel, automatically start playback.
            </div>
          </div>
          <input
            type="checkbox"
            checked={autoplay}
            onChange={(e) => setAutoplay(e.target.checked)}
            className="w-4 h-4 accent-accent"
          />
        </label>
      </section>

      {/* Channels data */}
      <section className="bg-bg-elevated border border-border rounded-lg p-4 space-y-4">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Database className="w-5 h-5" />
          Channels data
        </h2>
        <div className="text-sm text-text-muted">
          <div>Sports channels cached: {lastUpdated ? 'yes' : 'no'}</div>
          {lastUpdated && (
            <div className="text-xs">
              Last updated: {new Date(lastUpdated).toLocaleString()}
            </div>
          )}
        </div>
        <button
          onClick={() => {
            void refresh();
          }}
          disabled={isRefreshing}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-accent text-white rounded hover:bg-accent/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          {isRefreshing ? 'Refreshing…' : 'Refresh channels'}
        </button>
        <p className="text-xs text-text-muted">
          Re-downloads the sports / countries / categories lists and updates the
          local cache. The full channel list (2.6 MB) is loaded on demand from the
          All Channels page.
        </p>
      </section>

      {/* Local data */}
      <section className="bg-bg-elevated border border-border rounded-lg p-4 space-y-4">
        <h2 className="text-lg font-semibold">Local data</h2>
        <p className="text-sm text-text-muted">
          All your data is stored on this device. Nothing is uploaded.
        </p>

        <div className="space-y-2">
          <div className="flex items-center justify-between p-2 bg-bg rounded border border-border">
            <div className="flex items-center gap-2 text-sm">
              <Heart className="w-4 h-4 text-text-muted" />
              <span>Favorites</span>
              <span className="text-text-muted">({favoriteIds.length})</span>
            </div>
            {favoriteIds.length > 0 && (
              <button
                onClick={clearFavorites}
                className="text-xs px-2 py-1 text-error hover:bg-error/10 rounded transition-colors"
              >
                Clear
              </button>
            )}
          </div>

          <div className="flex items-center justify-between p-2 bg-bg rounded border border-border">
            <div className="flex items-center gap-2 text-sm">
              <Clock className="w-4 h-4 text-text-muted" />
              <span>Recents</span>
              <span className="text-text-muted">({recentEntries.length})</span>
            </div>
            {recentEntries.length > 0 && (
              <button
                onClick={clearRecents}
                className="text-xs px-2 py-1 text-error hover:bg-error/10 rounded transition-colors"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        <div className="pt-2 border-t border-border">
          {confirming === 'all' ? (
            <div className="flex items-center gap-2">
              <span className="text-sm text-text-muted">Clear all local data?</span>
              <button
                onClick={() => {
                  void handleClearAll();
                }}
                className="px-3 py-1.5 text-sm bg-error text-white rounded hover:bg-error/90 transition-colors"
              >
                Yes, clear everything
              </button>
              <button
                onClick={() => setConfirming(null)}
                className="px-3 py-1.5 text-sm bg-bg border border-border rounded hover:bg-bg-elevated transition-colors"
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              onClick={() => setConfirming('all')}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-bg border border-border rounded hover:bg-bg-elevated transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              Clear all local data
            </button>
          )}
        </div>
      </section>

      {/* About */}
      <section className="bg-bg-elevated border border-border rounded-lg p-4 space-y-2">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Info className="w-5 h-5" />
          About
        </h2>
        <p className="text-sm text-text-muted">
          StreamPitch is a sports-first live TV browser. Channels are pulled from
          the open-source iptv-org M3U list. Press{' '}
          <kbd className="px-1.5 py-0.5 bg-bg rounded border border-border text-xs">?</kbd>{' '}
          in the app to see keyboard shortcuts.
        </p>
      </section>
    </div>
  );
}
